import type { Properties } from 'js-java-properties'
import { getProperty, listProperties, setProperty } from 'js-java-properties'

import {
  LEGACY_SERIAL_CONNECTION_ID,
  namedModbusConnectionId,
} from '@/features/controller/configuration/models/constants'
import { MODBUS_METER_DEFAULTS } from '@/features/controller/configuration/models/raspiProperties/defaults'
import type { ConfigWithErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import { extractErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import {
  DEFAULT_MODBUS_TCP_PORT,
  readNamedModbusClients,
  readUnrecognisedModbusClientNames,
} from '@/features/controller/configuration/models/raspiProperties/namedModbusClients'
import {
  getSerialPortName,
  readBareOrFullSerialPortName,
} from '@/features/controller/configuration/models/raspiProperties/serialPort'
import type {
  LegacyModbusConnection,
  LegacyModbusMeter,
  LegacyModbusSerialConnection,
  LegacyModbusTcpConnection,
  ModbusActivePowerRegisters,
  ModbusRegister,
  ModbusTcpHostConfig,
  NamedModbusClient,
  NamedModbusConnection,
  SerialPort,
} from '@/features/controller/configuration/models/raspiProperties/types'
import {
  ModbusDataType,
  ModbusPowerRegisterVariant,
  ModbusRegisterType,
  WordOrder,
} from '@/features/controller/configuration/models/raspiProperties/types'
import {
  getOptionalTransformedPropertyOrError,
  readEnumProperty,
  readNumberProperty,
  readPositiveNumberProperty,
  removePropertiesWithPrefix,
} from '@/features/controller/configuration/models/raspiProperties/utils'

const PROPS_MODBUS_METERS_PREFIX = 'modbusMeter.meters.'

export function modbusDataTypeAddressStep(modbusDataType: ModbusDataType) {
  switch (modbusDataType) {
    case ModbusDataType.INT32:
    case ModbusDataType.FLOAT:
      return 2
    case ModbusDataType.INT16:
      return 1
  }
}

export function threePhaseActivePowerRegisterAddresses(address: number, dataType: ModbusDataType) {
  const step = modbusDataTypeAddressStep(dataType)
  return {
    phase1: address,
    phase2: address + 1 * step,
    phase3: address + 2 * step,
  }
}

function readRegisterAddressAndType(value: string): ConfigWithErrors<ModbusRegister | undefined> {
  const values = value.split(',')
  if (values.length !== 2) {
    return { config: undefined, errors: ['Expected register address followed by comma and register type'] }
  }
  const [addressRaw, registersRaw] = values
  const address = addressRaw.trim()
  const registers = registersRaw.trim()
  let addressNumber: number
  try {
    addressNumber = Number.parseInt(address, 10)
  } catch {
    return { config: undefined, errors: [`Invalid register address '${address}'`] }
  }
  let modbusRegisterType: ModbusRegisterType
  switch (registers) {
    case 'HOLDING_REGISTERS':
      modbusRegisterType = ModbusRegisterType.holding
      break
    case 'INPUT_REGISTERS':
      modbusRegisterType = ModbusRegisterType.input
      break
    default:
      return { config: undefined, errors: [`Invalid Modbus register type '${registers}'`] }
  }
  return {
    config: {
      address: addressNumber,
      registers: modbusRegisterType,
    },
  }
}

function readActivePowerRegisters(
  properties: Properties,
  prefix: string,
  dataType: ModbusDataType,
): ConfigWithErrors<ModbusActivePowerRegisters | undefined> {
  const errors: string[] = []
  const total = extractErrors(readModbusRegister(properties, `${prefix}.activePowerTotalRegister`), errors)
  const phase1 = extractErrors(readModbusRegister(properties, `${prefix}.activePowerPhase1Register`), errors)
  const phase2 = extractErrors(readModbusRegister(properties, `${prefix}.activePowerPhase2Register`), errors)
  const phase3 = extractErrors(readModbusRegister(properties, `${prefix}.activePowerPhase3Register`), errors)
  if (phase1 !== undefined && phase2 !== undefined && phase3 !== undefined) {
    if (phase2.registers !== phase1.registers || phase3.registers !== phase1.registers) {
      errors.push('All phase register types must be the same (HOLDING_REGISTERS or INPUT_REGISTERS)')
    }
    const { phase2: expectedPhase2Address, phase3: expectedPhase3Address } = threePhaseActivePowerRegisterAddresses(
      phase1.address,
      dataType,
    )
    if (phase2.address !== expectedPhase2Address) {
      errors.push(
        `When phase 1 register is ${phase1.address} with data type ${dataType}, phase 2 register must be ${expectedPhase2Address}`,
      )
    }
    if (phase3.address !== expectedPhase3Address) {
      errors.push(
        `When phase 1 register is ${phase1.address} with data type ${dataType}, phase 3 register must be ${expectedPhase3Address}`,
      )
    }
    return {
      config: {
        variant: ModbusPowerRegisterVariant.threePhase,
        ...phase1,
      },
      errors,
    }
  } else if (total) {
    return {
      config: {
        variant: ModbusPowerRegisterVariant.total,
        ...total,
      },
      errors,
    }
  } else {
    return {
      config: undefined,
      errors,
    }
  }
}

function writeModbusRegisterType(registers: ModbusRegisterType) {
  return registers === ModbusRegisterType.holding ? 'HOLDING_REGISTERS' : 'INPUT_REGISTERS'
}

function writeActivePowerRegisters(
  properties: Properties,
  prefix: string,
  activePowerRegisters: ModbusActivePowerRegisters | undefined,
  dataType: ModbusDataType,
) {
  if (activePowerRegisters !== undefined) {
    const address = activePowerRegisters.address
    const typeSuffix = `, ${writeModbusRegisterType(activePowerRegisters.registers)}`
    if (activePowerRegisters.variant === ModbusPowerRegisterVariant.threePhase) {
      const { phase1, phase2, phase3 } = threePhaseActivePowerRegisterAddresses(address, dataType)
      setProperty(properties, `${prefix}.activePowerPhase1Register`, `${phase1}${typeSuffix}`)
      setProperty(properties, `${prefix}.activePowerPhase2Register`, `${phase2}${typeSuffix}`)
      setProperty(properties, `${prefix}.activePowerPhase3Register`, `${phase3}${typeSuffix}`)
    } else {
      setProperty(properties, `${prefix}.activePowerTotalRegister`, `${address}${typeSuffix}`)
    }
  }
}

function readModbusRegister(properties: Properties, key: string) {
  return getOptionalTransformedPropertyOrError(properties, key, readRegisterAddressAndType)
}

function writeModbusRegister(properties: Properties, key: string, register?: ModbusRegister) {
  if (register) {
    const { address, registers } = register
    setProperty(properties, key, `${address}, ${writeModbusRegisterType(registers)}`)
  }
}

function readLegacyModbusMeter(
  properties: Properties,
  meterNumber: number,
): {
  meter: LegacyModbusMeter
  namedConnection?: string
  serialPort?: SerialPort
  tcpTarget?: ModbusTcpHostConfig
} {
  const prefix = PROPS_MODBUS_METERS_PREFIX + meterNumber
  const errors: string[] = []

  const namedConnection = getProperty(properties, `${prefix}.modbusConnection`)
  const host = getProperty(properties, `${prefix}.host`)
  // The port is the meter's own only when it has a host of its own: on a serial or named connection
  // the key is unused and the save drops it, so reading it would report a value nothing depends on
  const tcpTarget = host
    ? {
        host,
        port: extractErrors(
          readPositiveNumberProperty(properties, `${prefix}.port`, 'port', DEFAULT_MODBUS_TCP_PORT),
          errors,
        ),
      }
    : undefined
  const defaultDeviceNumber = MODBUS_METER_DEFAULTS.deviceNumber
  if (getProperty(properties, `${prefix}.deviceNumber`) === undefined) {
    errors.push(`Missing device number, assuming ${defaultDeviceNumber}`)
  }
  const serialPort = extractErrors(
    readBareOrFullSerialPortName(getProperty(properties, `${prefix}.serialPortName`)),
    errors,
  )
  // A missing device number is reported just above, so the reader is left with an unusable one
  const deviceNumber = extractErrors(
    readPositiveNumberProperty(properties, `${prefix}.deviceNumber`, 'device number', defaultDeviceNumber),
    errors,
  )
  const powerRegistersDataType = extractErrors(
    readEnumProperty(
      properties,
      `${prefix}.dataTypeOfPowerRegisters`,
      'power register data type',
      ModbusDataType,
      MODBUS_METER_DEFAULTS.powerRegistersDataType,
    ),
    errors,
  )
  return {
    meter: {
      activePowerRegisters: extractErrors(readActivePowerRegisters(properties, prefix, powerRegistersDataType), errors),
      activePowerScale: extractErrors(
        readPositiveNumberProperty(
          properties,
          `${prefix}.activePowerScale`,
          'active power scale',
          MODBUS_METER_DEFAULTS.activePowerScale,
        ),
        errors,
      ),
      dataTimestampRegister: extractErrors(readModbusRegister(properties, `${prefix}.dataTimestampRegister`), errors),
      deviceNumber: deviceNumber,
      errors: errors,
      meterErrorRegister: extractErrors(readModbusRegister(properties, `${prefix}.meterErrorRegister`), errors),
      number: meterNumber,
      powerMeasurementSkipPeriodInMilliseconds: extractErrors(
        readPositiveNumberProperty(
          properties,
          `${prefix}.powerMeasurementSkipPeriodInMillis`,
          'period between measurements',
          MODBUS_METER_DEFAULTS.powerMeasurementSkipPeriodInMilliseconds,
        ),
        errors,
      ),
      powerRegistersDataType,
      reactivePowerRegister: extractErrors(readModbusRegister(properties, `${prefix}.reactivePowerRegister`), errors),
      reactivePowerScale: extractErrors(
        readPositiveNumberProperty(
          properties,
          `${prefix}.reactivePowerScale`,
          'reactive power scale',
          MODBUS_METER_DEFAULTS.reactivePowerScale,
        ),
        errors,
      ),
      // Zero is a real offset here, and the default besides, so it is read like any other number
      registerAddressOffset: extractErrors(
        readNumberProperty(
          properties,
          `${prefix}.registerAddressOffset`,
          'register address offset',
          MODBUS_METER_DEFAULTS.registerAddressOffset,
        ),
        errors,
      ),
      systemTimestampRegister: extractErrors(
        readModbusRegister(properties, `${prefix}.systemTimestampRegister`),
        errors,
      ),
      // Falls back to the device number, which may itself be a stand-in, so the message can quote
      // a value the file never had -- still better than substituting one in silence
      unitId: extractErrors(
        readPositiveNumberProperty(properties, `${prefix}.unitId`, 'unit id', deviceNumber),
        errors,
      ),
      watchdogRegister: extractErrors(readModbusRegister(properties, `${prefix}.watchdogRegister`), errors),
      wordOrder: extractErrors(
        readEnumProperty(properties, `${prefix}.wordOrder`, 'word order', WordOrder, MODBUS_METER_DEFAULTS.wordOrder),
        errors,
      ),
    },
    namedConnection,
    serialPort,
    tcpTarget,
  }
}

function writeLegacyModbusMeter(properties: Properties, meter: LegacyModbusMeter, prefix: string) {
  setProperty(properties, `${prefix}.deviceNumber`, meter.deviceNumber.toString())
  setProperty(properties, `${prefix}.unitId`, meter.unitId?.toString())
  setProperty(properties, `${prefix}.registerAddressOffset`, meter.registerAddressOffset.toString())
  setProperty(properties, `${prefix}.wordOrder`, meter.wordOrder)
  writeActivePowerRegisters(properties, prefix, meter.activePowerRegisters, meter.powerRegistersDataType)
  setProperty(properties, `${prefix}.activePowerScale`, meter.activePowerScale.toString())
  writeModbusRegister(properties, `${prefix}.reactivePowerRegister`, meter.reactivePowerRegister)
  setProperty(properties, `${prefix}.reactivePowerScale`, meter.reactivePowerScale.toString())
  setProperty(properties, `${prefix}.dataTypeOfPowerRegisters`, meter.powerRegistersDataType)
  writeModbusRegister(properties, `${prefix}.watchdogRegister`, meter.watchdogRegister)
  writeModbusRegister(properties, `${prefix}.meterErrorRegister`, meter.meterErrorRegister)
  writeModbusRegister(properties, `${prefix}.systemTimestampRegister`, meter.systemTimestampRegister)
  writeModbusRegister(properties, `${prefix}.dataTimestampRegister`, meter.dataTimestampRegister)
  setProperty(
    properties,
    `${prefix}.powerMeasurementSkipPeriodInMillis`,
    meter.powerMeasurementSkipPeriodInMilliseconds.toString(),
  )
}

function writeLegacyModbusConnection(
  properties: Properties,
  conn: LegacyModbusConnection,
  globalSerialPort: SerialPort,
) {
  for (const meter of conn.meters) {
    const prefix = PROPS_MODBUS_METERS_PREFIX + meter.number
    if (conn['namedClient']) {
      // The endpoint lives on the connection, so the meter carries only the reference. Writing an
      // inline host as well would be a configuration error the controller rejects.
      setProperty(properties, `${prefix}.modbusConnection`, (conn as NamedModbusConnection).namedClient.name)
    }
    if (conn['tcpTarget']) {
      const tcpTarget = (conn as LegacyModbusTcpConnection).tcpTarget
      setProperty(properties, `${prefix}.host`, tcpTarget.host)
      setProperty(properties, `${prefix}.port`, tcpTarget.port.toString())
    }
    if (conn['serialPort']) {
      // Currently, we allow only 1 serial port per these legacy modules, so globalSerialPort is written
      setProperty(properties, `${prefix}.serialPortName`, getSerialPortName(globalSerialPort))
    }
    writeLegacyModbusMeter(properties, meter, prefix)
  }
}

/**
 * The meters the file declares, by number. A block named anything else is not a meter this model can
 * carry, so it is left out and reported rather than folded into meter 1: the save rewrites the
 * modelled meters over everything under the prefix, and would take the block with it unannounced.
 */
function readLegacyModbusMeterNumbers(properties: Properties): ConfigWithErrors<number[]> {
  const meterNumbers = new Set<number>()
  // By name, so a block is reported once rather than once per line in it
  const unnumbered = new Set<string>()
  for (const { key } of listProperties(properties).filter(({ key }) => key.startsWith(PROPS_MODBUS_METERS_PREFIX))) {
    const name = key.substring(PROPS_MODBUS_METERS_PREFIX.length).split('.')[0]
    const meterNumber = Number(name)
    if (Number.isInteger(meterNumber) && meterNumber > 0) {
      meterNumbers.add(meterNumber)
    } else {
      unnumbered.add(name)
    }
  }
  return {
    config: meterNumbers.values().toArray(),
    errors: unnumbered
      .values()
      .toArray()
      .map(
        (name) => `Ignoring Modbus meter '${name}': meter blocks are numbered, and the next save will remove this one`,
      ),
  }
}

function getNewOrPreviousConnection(
  previousConnections: LegacyModbusTcpConnection[],
  tcpTarget: ModbusTcpHostConfig,
  meterNumber: number,
) {
  const found = previousConnections.find(
    (conn) => conn.tcpTarget && conn.tcpTarget.host === tcpTarget.host && conn.tcpTarget.port === tcpTarget.port,
  )
  if (found) {
    return found
  } else {
    const newConnection: LegacyModbusTcpConnection = {
      errors: [],
      id: `modbus-tcp-${meterNumber}`,
      meters: [],
      tcpTarget: tcpTarget,
    }
    previousConnections.push(newConnection)
    return newConnection
  }
}

function makeNamedConnection(client: NamedModbusClient, errors: string[]): NamedModbusConnection {
  return {
    id: namedModbusConnectionId(client.name),
    errors,
    meters: [],
    namedClient: client,
  }
}

/**
 * A meter naming a connection nothing declares has no endpoint and no connection to sit under.
 * Inventing one would mean modelling something the file does not contain, so the meter is left out
 * of the model entirely and reported against the configuration as a whole. The write path clears
 * every meter block before rewriting the modelled ones, so this one does not survive a save, which
 * is what the message says. Unlike a device of another family, whose keys are never touched here.
 */
function describeDanglingReference(meterNumber: number, name: string, unrecognisedNames: string[]) {
  const why = unrecognisedNames.includes(name) ? 'is neither a Modbus/TCP nor a serial connection' : 'is not defined'
  return (
    `Modbus meter #${meterNumber} references Modbus connection '${name}', which ${why}. ` +
    'The meter is not shown below and will be removed when this configuration is saved.'
  )
}

function makeSerialConnection(serialPort: SerialPort): LegacyModbusSerialConnection {
  return {
    id: LEGACY_SERIAL_CONNECTION_ID,
    meters: [],
    errors: [],
    serialPort,
  }
}

function checkSerialPort(serialConnection: LegacyModbusSerialConnection, serialPort: SerialPort) {
  if (serialConnection.serialPort !== serialPort && serialConnection.errors.length === 0) {
    serialConnection.errors.push(
      'A meter is configured on a different serial port; it will be moved to the configured serial port when saved',
    )
  }
}

/** Endpoint ownership is exclusive, so a second endpoint is an error rather than a precedence rule. */
function checkNamedConnectionIsTheOnlyEndpoint(
  meter: LegacyModbusMeter,
  namedConnection: string,
  serialPort: SerialPort | undefined,
  tcpTarget: ModbusTcpHostConfig | undefined,
) {
  if (tcpTarget) {
    meter.errors.push(
      `Meter references Modbus connection '${namedConnection}' and also configures its own host; remove one of them`,
    )
  }
  if (serialPort) {
    meter.errors.push(`Meter references Modbus connection '${namedConnection}' and also has a serial port configured`)
  }
}

function attachToSerialConnection(
  meter: LegacyModbusMeter,
  serialConnection: LegacyModbusSerialConnection,
  serialPort: SerialPort | undefined,
) {
  if (serialPort) {
    checkSerialPort(serialConnection, serialPort)
  } else {
    meter.errors.push('Modbus meter without specified connection, assuming serial port')
  }
  serialConnection.meters.push(meter)
}

export function readLegacyModbusConnections(properties: Properties, globalSerialPort: SerialPort) {
  const errors: string[] = []
  const meterNumbers = extractErrors(readLegacyModbusMeterNumbers(properties), errors)
  const tcpConnections: LegacyModbusTcpConnection[] = []
  // Declared clients become connections whether or not a meter references them: they may serve
  // another device family, and an unused one still has to survive a save.
  const namedConnections: NamedModbusConnection[] = readNamedModbusClients(properties).map(({ config, errors }) =>
    makeNamedConnection(config, errors ?? []),
  )
  const unrecognisedNames = readUnrecognisedModbusClientNames(properties)
  const serialConnection = makeSerialConnection(globalSerialPort)
  for (const meterNumber of meterNumbers) {
    const { meter, namedConnection, serialPort, tcpTarget } = readLegacyModbusMeter(properties, meterNumber)
    if (namedConnection) {
      const connection = namedConnections.find((conn) => conn.namedClient.name === namedConnection)
      if (connection) {
        checkNamedConnectionIsTheOnlyEndpoint(meter, namedConnection, serialPort, tcpTarget)
        connection.meters.push(meter)
      } else {
        errors.push(describeDanglingReference(meterNumber, namedConnection, unrecognisedNames))
      }
    } else if (tcpTarget) {
      if (serialPort) {
        meter.errors.push('Meter with TCP host also has serial port configured')
      }
      getNewOrPreviousConnection(tcpConnections, tcpTarget, meterNumber).meters.push(meter)
    } else {
      attachToSerialConnection(meter, serialConnection, serialPort)
    }
  }
  return {
    connections: [serialConnection, ...tcpConnections, ...namedConnections],
    errors,
    highestMeterNumber: Math.max(...meterNumbers),
  }
}

export function writeLegacyModbusConnections(
  properties: Properties,
  legacyModbusConnections: LegacyModbusConnection[],
  globalSerialPort: SerialPort,
) {
  // The only way to properly maintain these via the UI is to have a complete model and write it all together in sequence
  removePropertiesWithPrefix(properties, PROPS_MODBUS_METERS_PREFIX)
  for (const conn of legacyModbusConnections) {
    writeLegacyModbusConnection(properties, conn, globalSerialPort)
  }
}
